import {
  Text,
  Skeleton,
  VStack,
  IconButton,
  Icon,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  useDisclosure,
  Tooltip,
} from '@wegroup/design-system';
import React, { ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculationSpec } from '../../../../../../../../../types/Quotes';
import { QuoteSpecifications } from '../../../../../../store/redux/slices/offersSlice';
import { toEuro } from '../../../../../../utils/stringUtils';
import OfferParameterSpec from './OfferParameterSpec';

export interface SpecFieldChange {
  field: string;
  value: unknown;
}

interface Props {
  label?: string | ReactElement;
  quoteSpecs?: QuoteSpecifications;
  calculationSpec?: CalculationSpec;
  isMediumFontWeight?: boolean;
  onChange: (change: SpecFieldChange) => void;
  isError?: boolean;
}

const OfferLabelValueParameter: React.FC<Props> = ({
  label,
  quoteSpecs,
  calculationSpec: calculationSpecProp,
  isMediumFontWeight,
  onChange,
  isError,
}) => {
  const [calculationSpec, setCalculationSpec] = useState<
    CalculationSpec | undefined
  >(calculationSpecProp);

  if (!calculationSpec && calculationSpecProp) {
    setCalculationSpec(calculationSpecProp);
  }

  if (label == null)
    return (
      <VStack align="start" spacing="1" minH="12">
        <Skeleton height="4" w="10" />
        <Skeleton height="4" w="20" />
      </VStack>
    );

  return (
    <VStack align="start" spacing="1" minH="12">
      <Text textStyle="smallBody" color="text.gray">
        {label}
      </Text>
      {isError && !calculationSpec ? (
        <Text
          textStyle={isMediumFontWeight ? 'normalBodyMedium' : 'normalBody'}
        >
          -
        </Text>
      ) : calculationSpec != null ? (
        <ValueField
          isMediumFontWeight={isMediumFontWeight}
          quoteSpecs={quoteSpecs}
          calculationSpec={calculationSpec}
          onChange={onChange}
        />
      ) : (
        <Skeleton height="4" w="20" />
      )}
    </VStack>
  );
};

interface ValueFieldProps {
  isMediumFontWeight?: boolean;
  quoteSpecs?: QuoteSpecifications;
  calculationSpec: CalculationSpec;
  onChange: (change: SpecFieldChange) => void;
}

const ValueField: React.FC<ValueFieldProps> = ({
  isMediumFontWeight,
  quoteSpecs,
  calculationSpec,
  onChange,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const methods = useForm({
    values: {
      [calculationSpec.calc_type]: calculationSpec.value,
    },
  });

  const handleSpecsChange = methods.handleSubmit((data) => {
    onChange({
      field: calculationSpec.specs_field,
      value: data[calculationSpec.calc_type],
    });
    onClose();
  });

  const specValue =
    quoteSpecs?.[calculationSpec.specs_field] != null
      ? quoteSpecs?.[calculationSpec.specs_field]
      : calculationSpec.value;

  return (
    <FormProvider {...methods}>
      <HStack>
        <Text
          textStyle={isMediumFontWeight ? 'normalBodyMedium' : 'normalBody'}
        >
          {specValue != null && toEuro(specValue)}
        </Text>
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <PopoverTrigger>
            <IconButton
              size="sm"
              icon={<Icon name="GeneralActionsEditDocument" />}
              aria-label="Edit"
              variant="unstyled"
            />
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton size="md" />
            <PopoverBody as={VStack} align="stretch" spacing="2">
              <HStack spacing="4" align="flex-end" pt="6">
                <OfferParameterSpec calcSpec={calculationSpec} />
                <IconButton
                  onClick={handleSpecsChange}
                  icon={<Icon name="GeneralStatusSuccessCheckmark" />}
                  aria-label="Confirm"
                />
              </HStack>
              {calculationSpec.specs_field === 'insured_art_value' &&
                methods.watch('INSURED_VALUE_ART') === 0 && (
                  <Text textStyle="smallBody" color="danger.main">
                    {t('VILLASURE_INSURED_VALUE_ART_WARNING-ZERO')}
                  </Text>
                )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
        {calculationSpec.specs_field === 'insured_art_value' &&
          methods.watch('INSURED_VALUE_ART') === 0 && (
            <Tooltip
              label={t('VILLASURE_INSURED_VALUE_ART_WARNING-ZERO')}
              shouldWrapChildren
            >
              <Icon name="GeneralStatusWarningTriangle" color="warning.main" />
            </Tooltip>
          )}
      </HStack>
    </FormProvider>
  );
};

export default OfferLabelValueParameter;
